"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import { AlertTriangle, Check, HelpCircle, Printer, Code2 } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { FOOD_STORAGE_DATA, OUTAGE_RULES, outageVerdict, type OutageVerdict } from "@/lib/food-storage-data"
import { useAlertSubscribe } from "@/components/price-watch/use-alert-subscribe"
import { BreakdownMomentCTA } from "@/components/warranty/breakdown-moment-cta"
import type { ApprovedWarrantyCta } from "@/lib/warranty/model"

// Email gate wired to the live P06 alert-engine tables (plan item 42,
// P06↔P07 stitch): POST /api/alerts/subscribe type 'outage_checklist' →
// outage_checklist_subscriptions. Double opt-in: the transactional confirmation
// email sends via Resend when configured, and no storm-season alert is
// dispatched until the recipient clicks the confirm link. The checklist itself
// stays ungated.

const DURATIONS = [
  { label: 'Under 4 hours', hours: 3 },
  { label: '4–6 hours', hours: 6 },
  { label: '6–24 hours', hours: 12 },
  { label: 'Over 24 hours', hours: 30 },
] as const

const VERDICT_STYLE: Record<OutageVerdict, { label: string; color: string; bg: string }> = {
  keep: { label: 'Keep', color: '#10a65c', bg: 'rgba(16,166,92,0.08)' },
  discard: { label: 'Toss', color: '#dc2626', bg: 'rgba(220,38,38,0.08)' },
  check: { label: 'Check', color: '#d97706', bg: 'rgba(217,119,6,0.08)' },
}

const CHECKLIST_ITEMS = [
  'Keep fridge and freezer doors closed — every opening costs ≈30 minutes of safe time',
  `Fridge is safe up to ${OUTAGE_RULES.fridgeSafeHoursClosed} hours without power (door closed)`,
  `Full freezer: safe ≈${OUTAGE_RULES.freezerFullSafeHours} hours · half-full: ≈${OUTAGE_RULES.freezerHalfSafeHours} hours`,
  `After 4+ hours: toss meat, poultry, fish, eggs, milk, soft cheese, leftovers, cut produce`,
  'Keep: butter, hard cheese, whole produce, bread, and high-acid condiments (ketchup, mustard, pickles)',
  'Refreeze only food that still has ice crystals or reads 40°F or below',
  'Never taste food to check it — pathogens don\'t change taste or smell',
  'Use an appliance thermometer to verify: fridge ≤40°F, freezer ≤0°F',
  'Photograph discarded food for insurance claims before you toss it',
  'When in doubt, throw it out',
]

export function PowerOutageCenterPageClient({
  approvedWarrantyCtas = [],
}: {
  /** FRIDGE-1042 — approved warranty programs from the registry; [] renders no slot. */
  approvedWarrantyCtas?: ApprovedWarrantyCta[]
} = {}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(['raw-chicken', 'milk', 'butter']))
  const [duration, setDuration] = useState<number>(6)
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [optIn, setOptIn] = useState(false)
  const { status: gateStatus, errorMessage: gateError, subscribe } = useAlertSubscribe()

  const verdicts = useMemo(
    () =>
      FOOD_STORAGE_DATA.filter((f) => selected.has(f.key)).map((f) => ({
        entry: f,
        verdict: outageVerdict(f, duration),
      })),
    [selected, duration],
  )

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleGateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void subscribe({
      type: 'outage_checklist',
      email,
      honeypot,
      generalUpdatesOptIn: optIn,
    })
  }

  return (
    <ToolPageShell
      toolName="Power Outage Food Safety Center"
      heroEyebrow="Storm center"
      heroTitlePre="Power's out."
      heroTitleItalic="Keep or toss"
      heroTitlePost="— food by food."
      heroSubtitle="Select what's in your fridge and how long the power's been out — get USDA/FSIS verdicts for every item, plus a printable checklist to stick on the door before the next storm."
      heroMeta={["⏱ ~60 sec", "USDA FSIS + FoodSafety.gov charts"]}
      heroShare={<ToolShareBar title="Power outage: what's safe to eat? Food-by-food USDA triage" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The FSIS rules
          </div>
          <p className="text-[17px] font-medium leading-[1.45] mb-5" style={{ textWrap: "balance" as never }}>
            Doors <em className="italic text-[#ff9951]">closed</em>: fridge{" "}
            <em className="italic text-[#ff9951]">4 hours</em>, full freezer{" "}
            <em className="italic text-[#ff9951]">48</em>, half-full{" "}
            <em className="italic text-[#ff9951]">24</em>.
          </p>
          <div className="grid grid-cols-3 gap-3 pt-4 border-t border-white/10">
            {[["Fridge", "4 hrs"], ["Full freezer", "48 hrs"], ["Half-full", "24 hrs"]].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">{k}</div>
                <div className="text-[20px] font-black tracking-tight">{v}</div>
              </div>
            ))}
          </div>
        </div>
      }
      faqs={[
        {
          question: "How long is food safe in the fridge during an outage?",
          answer:
            "Per USDA FSIS, a closed refrigerator keeps food at a safe temperature for up to 4 hours. After 4 hours without power, discard perishables — meat, poultry, fish, eggs, milk, soft cheeses, leftovers, and cut produce.",
        },
        {
          question: "Can I refreeze food that thawed during the outage?",
          answer:
            "Yes, if it still contains ice crystals or reads 40°F or below on a thermometer. Quality may suffer, but safety is intact. Exception: never refreeze thawed ice cream or frozen yogurt.",
        },
        {
          question: "Which foods survive an outage no matter what?",
          answer:
            "Butter and margarine, hard and processed cheeses, whole (uncut) fruits and vegetables, bread and most baked goods, and high-acid condiments like ketchup, mustard, pickles, and jam — per the FoodSafety.gov chart.",
        },
        {
          question: "Is the 4-hour clock different for mayonnaise?",
          answer:
            "Slightly — FSIS says discard opened mayonnaise, tartar sauce, and horseradish if held above 50°F for more than 8 hours. Everything else perishable follows the 40°F / 4-hour rule.",
        },
        {
          question: "Where do these verdicts come from?",
          answer:
            "Every verdict maps to the USDA FSIS 'Keep Your Food Safe During Emergencies' guidance and the FoodSafety.gov power-outage charts — the same rules, food by food, with nothing invented.",
        },
      ]}
      relatedTools={[
        {
          title: "Power Outage Food Safety Guide",
          category: "Food Storage",
          description: "The long-form USDA guide.",
          href: "/tools/power-outage-food-safety-guide",
          time: "~4 min",
        },
        {
          title: "Dry Ice Calculator",
          category: "Food Storage",
          description: "How much dry ice for your freezer.",
          href: "/tools/freezer-dry-ice-calculator",
          time: "~30 sec",
        },
        {
          title: "Generator Size Calculator",
          category: "Energy & Cost",
          description: "Watts needed to run your fridge.",
          href: "/tools/refrigerator-generator-size-calculator",
          time: "~45 sec",
        },
        {
          title: "Food Storage Calculator",
          category: "Food Storage",
          description: "Shelf life by food category.",
          href: "/tools/food-storage-calculator",
          time: "~15 sec",
        },
      ]}
      cta={{
        title: "Outage-prone area?",
        body: "A full freezer holds safe temps twice as long as a half-full one. The right unit lets you sleep through a 48-hour outage without losing a thing.",
        buttonLabel: "Browse all freezers",
        buttonHref: "/best-freezers",
      }}
    >
      {/* Print stylesheet: printing shows ONLY the checklist card */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #outage-print-checklist, #outage-print-checklist * { visibility: visible; }
          #outage-print-checklist { position: absolute; left: 0; top: 0; width: 100%; }
        }
      `}</style>

      {/* ============ FSIS RULES RECAP ============ */}
      <section className="py-8 md:py-10 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <h2
            className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
            style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
          >
            The three rules (USDA FSIS)
          </h2>
          <ol className="list-none p-0 m-0 grid md:grid-cols-3 gap-4">
            {[
              {
                n: "01",
                t: "Keep doors closed",
                d: `A closed refrigerator stays safe ≈${OUTAGE_RULES.fridgeSafeHoursClosed} hours. A full freezer ≈${OUTAGE_RULES.freezerFullSafeHours} hours; half-full ≈${OUTAGE_RULES.freezerHalfSafeHours}.`,
              },
              {
                n: "02",
                t: "40°F is the line",
                d: "Perishables held above 40°F too long get discarded — meat, poultry, fish, eggs, dairy, leftovers, cut produce.",
              },
              {
                n: "03",
                t: "Verify, don't taste",
                d: "Pathogens don't change taste or smell. Use an appliance thermometer; when in doubt, throw it out.",
              },
            ].map((s) => (
              <li key={s.n} className="p-5 rounded-xl border border-gray-200 bg-gray-50">
                <div className="font-mono text-sm font-bold text-[#ff9951] mb-2">{s.n}</div>
                <h3 className="text-[18px] font-extrabold tracking-[-0.015em] text-gray-900 mb-1">{s.t}</h3>
                <p className="text-[14px] text-gray-600 leading-[1.55] m-0">{s.d}</p>
              </li>
            ))}
          </ol>
          <p className="text-[13px] text-gray-500 mt-4">
            Sources:{" "}
            <a href="https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/emergencies/keep-your-food-safe-during-emergencies" target="_blank" rel="noopener noreferrer" className="underline">
              USDA FSIS, Keep Your Food Safe During Emergencies
            </a>{" "}
            ·{" "}
            <a href="https://www.foodsafety.gov/food-safety-charts/food-safety-during-power-outage" target="_blank" rel="noopener noreferrer" className="underline">
              FoodSafety.gov power-outage charts
            </a>{" "}
           .
          </p>
        </div>
      </section>

      {/* ============ TRIAGE TOOL ============ */}
      <section className="py-8 md:py-10 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <h2
            className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
            style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
          >
            What to toss — triage your fridge
          </h2>

          <div className="grid md:grid-cols-[1fr_1fr] gap-8">
            <div>
              <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-3">
                1 · How long has the power been out?
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                {DURATIONS.map((d) => (
                  <button
                    key={d.hours}
                    type="button"
                    onClick={() => setDuration(d.hours)}
                    className="px-4 py-2 rounded-full text-[14px] font-semibold border transition-colors"
                    style={
                      duration === d.hours
                        ? { background: "#0469f0", color: "#fff", borderColor: "#0469f0" }
                        : { background: "#fff", color: "#374151", borderColor: "#e5e7eb" }
                    }
                  >
                    {d.label}
                  </button>
                ))}
              </div>

              <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-3">
                2 · What&apos;s inside? (select all that apply)
              </div>
              <div className="flex flex-wrap gap-2">
                {FOOD_STORAGE_DATA.map((f) => {
                  const on = selected.has(f.key)
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => toggle(f.key)}
                      aria-pressed={on}
                      className="px-3 py-1.5 rounded-full text-[13px] font-medium border transition-colors"
                      style={
                        on
                          ? { background: "rgba(255,153,81,0.12)", color: "#c2570f", borderColor: "#ff9951" }
                          : { background: "#fff", color: "#4b5563", borderColor: "#e5e7eb" }
                      }
                    >
                      {f.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div>
              <div className="text-[13px] font-bold uppercase tracking-[0.12em] text-gray-500 mb-3">
                3 · Verdicts
              </div>
              {verdicts.length === 0 ? (
                <p className="text-gray-500 text-[14px]">Select foods on the left to see keep/toss verdicts.</p>
              ) : (
                <ul className="list-none p-0 m-0 space-y-2" data-testid="triage-verdicts">
                  {verdicts.map(({ entry, verdict }) => {
                    const style = VERDICT_STYLE[verdict]
                    const Icon = verdict === 'keep' ? Check : verdict === 'discard' ? AlertTriangle : HelpCircle
                    return (
                      <li
                        key={entry.key}
                        className="flex items-start gap-3 p-3 rounded-lg border"
                        style={{ background: style.bg, borderColor: `${style.color}33` }}
                      >
                        <span
                          className="flex items-center gap-1 flex-shrink-0 px-2 py-0.5 rounded font-bold text-[12px] uppercase tracking-wide"
                          style={{ color: '#fff', background: style.color }}
                        >
                          <Icon className="w-3.5 h-3.5" /> {style.label}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[14px] font-bold text-gray-900">{entry.label}</div>
                          <div className="text-[13px] text-gray-600 leading-[1.5]">{entry.outageDetail}</div>
                          {duration > OUTAGE_RULES.fridgeSafeHoursClosed && (
                            <div className="text-[12px] text-gray-500 mt-0.5">{entry.refreezeGuidance}</div>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
              )}
              <p className="text-[12px] text-gray-500 mt-3">
                Verdicts follow the FSIS 4-hour rule and the FoodSafety.gov per-food charts — never taste to check.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ BREAKDOWN-MOMENT SLOT (FRIDGE-1042) — nothing until approved ============ */}
      <BreakdownMomentCTA programs={approvedWarrantyCtas} context="the outage fresh in mind" />

      {/* ============ PRINTABLE CHECKLIST + EMAIL GATE ============ */}
      <section className="py-8 md:py-10 bg-gray-50">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[1.2fr_0.8fr] gap-8 items-start">
          <div id="outage-print-checklist" className="bg-white rounded-xl border border-gray-900 p-6 md:p-8">
            <div className="flex items-center justify-between gap-4 mb-4">
              <h2 className="font-black text-gray-900 m-0" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
                The fridge-door checklist
              </h2>
              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-bold text-white print:hidden"
                style={{ background: "#0469f0" }}
              >
                <Printer className="w-4 h-4" /> Print
              </button>
            </div>
            <ol className="list-none p-0 m-0 space-y-2.5">
              {CHECKLIST_ITEMS.map((t, i) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="font-mono text-[12px] font-bold text-[#ff9951] pt-0.5 min-w-[22px]">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-[14px] text-gray-800 leading-[1.55]">{t}</span>
                </li>
              ))}
            </ol>
            <p className="text-[11px] text-gray-500 mt-5 mb-0">
              USDA FSIS + FoodSafety.gov guidance · fridge.com/tools/power-outage-center
            </p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-extrabold text-gray-900 text-[17px] mb-2 mt-0">Get storm-season alerts</h3>
            <p className="text-[13px] text-gray-600 leading-[1.55]">
              Optional — the checklist is free either way. Leave an email and we&apos;ll send storm-season
              food-safety reminders once email alerts launch.
            </p>
            {gateStatus === 'success' ? (
              <div
                className="flex items-start gap-2 p-3 rounded-lg"
                style={{ background: "rgba(16,166,92,0.08)" }}
                data-testid="outage-checklist-form"
                data-state="success"
              >
                <Check className="w-4 h-4 text-[#10a65c] mt-0.5 flex-shrink-0" />
                <p className="text-[13px] text-gray-700 m-0">
                  Almost there — check your inbox. We just emailed you a confirmation link; click it to
                  activate your storm-season alerts. Nothing arrives until you confirm. Print the
                  checklist on the left any time.
                </p>
              </div>
            ) : (
              <form onSubmit={handleGateSubmit} className="flex flex-col gap-2" data-testid="outage-checklist-form">
                <label htmlFor="outage-email" className="sr-only">Email address</label>
                <input
                  id="outage-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2.5 text-[14px] border border-gray-200 rounded-lg outline-none"
                />
                {/* Honeypot — hidden from real users; bots that fill it are rejected. */}
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <label className="flex items-start gap-2 text-[12px] text-gray-600">
                  <input
                    type="checkbox"
                    checked={optIn}
                    onChange={(e) => setOptIn(e.target.checked)}
                    className="mt-0.5"
                  />
                  Also send me occasional Fridge.com updates
                </label>
                <button
                  type="submit"
                  disabled={gateStatus === 'submitting' || !email.trim()}
                  className="px-4 py-2.5 rounded-lg text-[14px] font-bold text-white disabled:opacity-50"
                  style={{ background: "#ff9951" }}
                >
                  {gateStatus === 'submitting' ? 'Saving…' : 'Save my signup'}
                </button>
                {gateStatus === 'error' && gateError && (
                  <p className="text-[12px] text-red-600 m-0" role="alert">
                    {gateError}
                  </p>
                )}
              </form>
            )}
            <p className="text-[11px] text-gray-500 mt-3 leading-[1.5]">
              Disclosure: product links elsewhere on this page may earn us an affiliate
              commission at no cost to you. We never sell your email; see our{" "}
              <Link href="/privacy-policy" className="underline">privacy policy</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ============ EMBED PROMO ============ */}
      <section className="py-8 md:py-10 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="rounded-xl border border-gray-200 p-6 md:p-8 grid md:grid-cols-[auto_1fr] gap-5 items-start">
            <div className="w-12 h-12 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0">
              <Code2 className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-black text-gray-900 m-0 mb-2" style={{ fontSize: 22, letterSpacing: "-0.02em" }}>
                Newsrooms: embed this checklist free
              </h2>
              <p className="text-[14px] text-gray-600 leading-[1.6] mb-3">
                Covering a storm? Drop the USDA checklist into any article — two lines of HTML, under 5 KB,
                no ads, no tracking, works during the outage.
              </p>
              <pre className="bg-gray-900 text-gray-100 text-[12.5px] rounded-lg p-4 overflow-x-auto m-0">
{`<div id="fridge-power-outage-checklist"></div>
<script src="https://fridge.com/widgets/power-outage-checklist.js"></script>`}
              </pre>
              <p className="text-[12px] text-gray-500 mt-2 mb-0">
                Only condition: keep the &ldquo;Powered by Fridge.com&rdquo; attribution intact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ PRODUCT CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Outage-ready storage"
          title="Chest freezers built for the 48-hour rule"
          description="The FSIS 48-hour window assumes a full, closed freezer — chest layouts hold cold longest when the power's out."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
